<template>
  <div
    v-if="!loading || items.length"
    class="puzzle-list-view"
  >
    <div
      v-for="(item, i) in items"
      :key="item.id ?? `slotted-object-${i}`"
      :data-index="i"
      class="puzzle-list-item"
      @click="handleItemClick(item, i)"
    >
      <slot
        :position="i"
        :item="item"
      />
    </div>
  </div>
  <div
    v-else-if="loading"
    class="puzzle-list-view"
  >
    <div
      v-for="i in numberOfSkeletonCards"
      :key="i - 1"
      class="puzzle-list-item"
    >
      <slot
        name="skeleton"
        :position="i - 1"
      />
    </div>
  </div>

  <div
    v-if="moreButton"
    class="btn-wrap"
  >
    <UiButtonV3
      data-test="articles-sights-btn-more"
      class="btn"
      :variant="UiButtonVariant.OUTLINE"
      :color-mode="UiButtonColorMode.NEUTRAL"
      :text="moreButtonText"
      :href="moreButtonLink"
      :is-loading="loading"
      @click.prevent="handleLoadMoreClick"
    />
  </div>
</template>

<script lang="ts" setup generic="T extends { id: string | number }">
  import { UiButtonV3, UiButtonColorMode, UiButtonVariant } from '@experience/vue3-kit';

  interface Props<T> {
    /**
     * Список элементов.
     */
    items: T[];

    /**
     * Показывать кнопку "еще".
     *
     * @default false
     */
    moreButton?: boolean;

    /**
     * Текст кнопки "еще".
     */
    moreButtonText: string;

    /**
     * Ссылка для кнопки "еще".
     */
    moreButtonLink?: string;

    /**
     * Маркер для E2E-тестов.
     */
    moreDataTest?: string;

    /**
     * Флаг загрузки.
     *
     * @default false
     */
    loading?: boolean;

    /**
     * Количество отображаемых скелетонов в режиме загрузки.
     *
     * @default 2
     */
    numberOfSkeletonCards?: number;
  }

  withDefaults(defineProps<Props<T>>(), {
    moreButton: false,
    loading: false,
    numberOfSkeletonCards: 2,
  });

  defineSlots<{
    /**
     * Слот для элемента.
     */
    default: (props: {
      /**
       * Позиция в списке.
       */
      position: number;

      /**
       * Данные элемента.
       */
      item: T;
    }) => unknown;

    /**
     * Слот для скелетона элемента.
     */
    skeleton: (props: {
      /**
       * Позиция в списке.
       */
      position: number;
    }) => unknown;
  }>();

  const emit = defineEmits<{
    /**
     * Событие клика по кнопке "еще".
     */
    'load-more': [];
    /**
     * Событие клика по элементу.
     *
     * @param item данные элемента
     * @param position позиция в списке
     */
    'item-click': [item: T, position: number];
  }>();

  const handleLoadMoreClick = () => emit('load-more');

  const handleItemClick = async (item: T, position: number) => emit('item-click', item, position);
</script>

<style lang="scss" scoped>
  .puzzle-list-view {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 48px 24px;
    padding-top: 48px;
    color: var(--alias-color-text-default);

    @include tablet {
      row-gap: 32px;
      padding-top: 40px;
    }

    @include mobile {
      display: flex;
      flex-direction: column;
    }
  }

  .puzzle-list-item {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: fit-content;
    cursor: pointer;

    &:nth-child(5n + 1) {
      grid-column: 1/7;

      :deep(.puzzle-article-item-photo) {
        aspect-ratio: 1/1;
      }
    }

    &:nth-child(5n + 2) {
      grid-column: 7/13;

      :deep(.puzzle-article-item-photo) {
        aspect-ratio: 1/1;
      }
    }

    &:nth-child(5n + 3) {
      grid-column: 1/5;

      :deep(.puzzle-article-item-photo) {
        aspect-ratio: 4/3;
      }
    }

    &:nth-child(5n + 4) {
      grid-column: 5/9;

      :deep(.puzzle-article-item-photo) {
        aspect-ratio: 4/3;
      }
    }

    &:nth-child(5n + 5) {
      grid-column: 9/13;

      :deep(.puzzle-article-item-photo) {
        aspect-ratio: 4/3;
      }
    }

    &:hover {
      :deep(.puzzle-article-item-photo img) {
        transform: scale(1.1);
      }
    }

    @include tablet {
      gap: 8px;
    }
  }

  .btn-wrap {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 24px;
    margin: 24px 0 0;

    .btn {
      grid-column: 5/9;

      @include tablet {
        grid-column: 4/10;
      }
    }

    @include mobile {
      display: flex;
      flex-direction: column;
    }
  }
</style>
